/**
 * Unlimited Floor Sheet operator rows (equipment + optional CW/CCW + team member).
 * `rows` is the source of truth; `operatorMap` is kept in sync for older readers.
 */

export type OperatorDirection = 'CW' | 'CCW' | ''

export type FloorSheetOperatorRow = {
  id: string
  equipment: string
  direction: OperatorDirection
  name: string
  memberId: string
}

const DIRECTIONAL_TOKENS = new Set(['P600Z', 'P600', 'P625', 'M500', 'M300'])

export function normalizeEquipmentToken(value: string): string {
  return value.replace(/[^a-z0-9]/gi, '').toUpperCase()
}

export function isDirectionalEquipment(equipment: string): boolean {
  return DIRECTIONAL_TOKENS.has(normalizeEquipmentToken(equipment))
}

export function newOperatorRowId(): string {
  return `op-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`
}

export function emptyOperatorRow(): FloorSheetOperatorRow {
  return {
    id: newOperatorRowId(),
    equipment: '',
    direction: '',
    name: '',
    memberId: ''
  }
}

/** Ensure a Recorded By row exists once (required for Floor Sheet PDF). */
export function ensureRecordedByOperatorRow(rows: FloorSheetOperatorRow[]): FloorSheetOperatorRow[] {
  const has = rows.some((r) => fieldKeyFromOperatorRow(r) === 'Recorded')
  if (has) return rows
  return [
    {
      id: newOperatorRowId(),
      equipment: '',
      direction: '',
      name: '',
      memberId: ''
    },
    ...rows
  ]
}

/** Ensure a Root Misfire Welder (Semi-Automatic) row exists once. */
export function ensureRootMisfireOperatorRow(rows: FloorSheetOperatorRow[]): FloorSheetOperatorRow[] {
  const has = rows.some(
    (r) => normalizeEquipmentToken(r.equipment) === 'SEMIAUTOMATICWELDING'
  )
  if (has) return rows
  return [
    ...rows,
    {
      id: newOperatorRowId(),
      equipment: 'Semi-Automatic Welding',
      direction: '',
      name: '',
      memberId: ''
    }
  ]
}

/** Ensure an IWM Operator row exists once. */
export function ensureIwmOperatorRow(rows: FloorSheetOperatorRow[]): FloorSheetOperatorRow[] {
  const has = rows.some((r) => normalizeEquipmentToken(r.equipment) === 'IWM')
  if (has) return rows
  return [
    ...rows,
    {
      id: newOperatorRowId(),
      equipment: 'IWM',
      direction: '',
      name: '',
      memberId: ''
    }
  ]
}

function isEmptyIwmRow(row: FloorSheetOperatorRow): boolean {
  return normalizeEquipmentToken(row.equipment) === 'IWM' && !row.name.trim() && !row.memberId.trim()
}

function isEmptySemiAutoRow(row: FloorSheetOperatorRow): boolean {
  return (
    normalizeEquipmentToken(row.equipment) === 'SEMIAUTOMATICWELDING' &&
    !row.name.trim() &&
    !row.memberId.trim()
  )
}

/**
 * Seed / prune root-equipment operator rows for Floor Sheet Operators Info.
 * Keeps directional and other equipment rows (including blank rows from
 * "Add welder"); strips only the empty opposite root-equipment type when
 * switching IWM vs Root Misfire modes.
 */
export function syncOperatorRowsForRootEquipment(
  rows: FloorSheetOperatorRow[],
  rootEquipment: 'iwm' | 'iwm_and_rc'
): FloorSheetOperatorRow[] {
  let next = [...rows]
  if (rootEquipment === 'iwm_and_rc') {
    next = next.filter((r) => !isEmptyIwmRow(r))
    next = ensureRootMisfireOperatorRow(next)
  } else {
    next = next.filter((r) => !isEmptySemiAutoRow(r))
    next = ensureIwmOperatorRow(next)
  }
  return next
}

/** Storage / focus key matching historic operatorMap keys. */
export function fieldKeyFromOperatorRow(row: Pick<FloorSheetOperatorRow, 'equipment' | 'direction'>): string {
  const equipment = row.equipment.trim()
  if (!equipment) return 'Recorded'
  if (normalizeEquipmentToken(equipment) === 'IWM') return 'IWM'
  if (normalizeEquipmentToken(equipment) === 'MANUALWELDING') return 'Manual Welding'
  if (normalizeEquipmentToken(equipment) === 'SEMIAUTOMATICWELDING') return 'Semi-Automatic Welding'
  if (row.direction === 'CW' || row.direction === 'CCW') return `${equipment} ${row.direction}`
  return equipment
}

export function roleLabelFromOperatorRow(row: Pick<FloorSheetOperatorRow, 'equipment' | 'direction'>): string {
  const key = fieldKeyFromOperatorRow(row)
  if (key === 'IWM') return 'IWM Operator'
  if (key === 'Manual Welding') return 'Manual Welding Operator'
  if (key === 'Semi-Automatic Welding') return 'Root Misfire Welder'
  if (key === 'Recorded') return 'Recorded By'
  const m = key.match(/^(.+) (CW|CCW)$/)
  if (m) return `${m[1]} ${m[2]} Welder`
  return `${key} Operator`
}

/** User-facing equipment label — Semi-Automatic stores as-is but displays as Root Misfire Welder. */
export function equipmentDisplayLabel(equipment: string): string {
  if (normalizeEquipmentToken(equipment) === 'SEMIAUTOMATICWELDING') return 'Root Misfire Welder'
  return equipment.trim()
}

export function migrateOperatorMapToRows(
  operatorMap?: Record<string, { name?: string; id?: string }>,
  equipment?: string[]
): FloorSheetOperatorRow[] {
  const map = operatorMap && typeof operatorMap === 'object' ? operatorMap : {}
  const rows: FloorSheetOperatorRow[] = []
  const seen = new Set<string>()

  for (const [key, entry] of Object.entries(map)) {
    const name = String(entry?.name ?? '').trim()
    const memberId = String(entry?.id ?? '').trim()
    if (!name && !memberId) continue
    const parsed = parseOperatorMapKey(key)
    rows.push({
      id: newOperatorRowId(),
      equipment: parsed.equipment,
      direction: parsed.direction,
      name,
      memberId
    })
    seen.add(key)
  }

  if (rows.length > 0) return rows

  const chips = Array.isArray(equipment) ? equipment.filter((e) => String(e).trim()) : []
  if (chips.length === 0) return []
  return chips.map((equip) => ({
    id: newOperatorRowId(),
    equipment: String(equip),
    direction: isDirectionalEquipment(String(equip)) ? 'CW' : '',
    name: '',
    memberId: ''
  }))
}

export function parseOperatorMapKey(fieldKey: string): { equipment: string; direction: OperatorDirection } {
  if (fieldKey === 'IWM') return { equipment: 'IWM', direction: '' }
  if (fieldKey === 'Manual Welding') return { equipment: 'Manual Welding', direction: '' }
  if (fieldKey === 'Semi-Automatic Welding') return { equipment: 'Semi-Automatic Welding', direction: '' }
  if (fieldKey === 'Recorded') return { equipment: '', direction: '' }
  const m = fieldKey.match(/^(.+) (CW|CCW)$/)
  if (m) return { equipment: m[1], direction: m[2] as 'CW' | 'CCW' }
  return { equipment: fieldKey, direction: '' }
}

export function syncOperatorMapFromRows(
  rows: FloorSheetOperatorRow[]
): Record<string, { name: string; id: string }> {
  const map: Record<string, { name: string; id: string }> = {}
  for (const row of rows) {
    const key = fieldKeyFromOperatorRow(row)
    if (map[key]) continue
    if (!row.name.trim() && !row.memberId.trim()) continue
    map[key] = { name: row.name, id: row.memberId }
  }
  return map
}

export function equipmentListFromRows(rows: FloorSheetOperatorRow[]): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const row of rows) {
    const equip = row.equipment.trim()
    if (!equip || seen.has(equip)) continue
    seen.add(equip)
    out.push(equip)
  }
  return out
}

export function normalizeOperatorRows(raw: unknown, fallbackMap?: Record<string, { name?: string; id?: string }>, fallbackEquipment?: string[]): FloorSheetOperatorRow[] {
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((item, index) => {
      const obj = item && typeof item === 'object' ? (item as Record<string, unknown>) : {}
      const directionRaw = String(obj.direction ?? '')
      const direction: OperatorDirection =
        directionRaw === 'CW' || directionRaw === 'CCW' ? directionRaw : ''
      return {
        id: String(obj.id || `op-norm-${index}`),
        equipment: String(obj.equipment ?? ''),
        direction,
        name: String(obj.name ?? ''),
        memberId: String(obj.memberId ?? '')
      }
    })
  }
  return migrateOperatorMapToRows(fallbackMap, fallbackEquipment)
}
